/**
 * @file CASARVoucher Component
 * @description This component renders a PDF viewer for CAS AR (Acknowledgement Receipt) vouchers.
 * It fetches AR details based on the provided AR number and displays them in a formatted PDF document.
 */

import FormPDF, { IFormInput, IFormPDFRef } from '@components/pdf/form-pdf';
import { TableRow } from '@components/pdf/react-pdf/react-pdf.interface';
import useAppContext from '@hooks/useAppContext';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToWords } from 'to-words';
import { useLazyGetCASTransactionQuery } from '../../api/cas/cas.api.rtk';
import useToast from '../../hooks/useToast';
import { CASGetARDetails } from '../../interfaces/cas.interface';
import {
  dateTimeFormat,
  formatNumber,
  toReportInputs,
} from '../../utils/format';

/**
 * @typedef {Object} CASARVoucherProps
 * @property {number|string} [ar_no='0'] - The AR number to fetch details for
 * @property {boolean} [isOpen=true] - Whether the voucher modal is open
 * @property {() => void} [onClose] - Function to call when closing the voucher
 * @property {string} [tranType='AR'] - The transaction type
 * @property {boolean} [isReprint= true] - if the AR is for reprint
 */

const toWords = new ToWords({
  localeCode: 'en-PH',
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: false,
    currencyOptions: {
      name: 'Peso',
      plural: 'Pesos',
      symbol: '₱',
      fractionalUnit: {
        name: 'Centavo',
        plural: 'Centavos',
        symbol: '',
      },
    },
  },
});

/**
 * CASARVoucher component
 * @param {CASARVoucherProps} props - The component props
 */
const CASARVoucher = ({
  ar_no = '0',
  isOpen = true,
  onClose,
  tranType = 'AR',
  reprint,
}: {
  ar_no?: number | string;
  isOpen?: boolean;
  onClose?: () => void;
  tranType?: string;
  reprint?: boolean;
}) => {
  const navigate = useNavigate();

  const handleCloseClick = () => {
    ar_no = 0;
    !onClose && navigate(-1);
    onClose?.();
  };

  const {
    genpar: { getGenparValue },
    startLoading,
    stopLoading,
  } = useAppContext();

  const casAcn = getGenparValue('cas_acn');
  const casAcnDate = getGenparValue('cas_acn_date');
  const arSeries = getGenparValue('ar_series');

  const { toast } = useToast();
  const [CASARData] = useLazyGetCASTransactionQuery();

  const formRef = useRef<IFormPDFRef>(null);

  const handlePrint = async ({
    ar_no = '0',
    tranType = 'AR',
  }: {
    ar_no?: number | string;
    tranType?: string;
  }) => {
    try {
      startLoading();
      if (!formRef.current) {
        toast({ severity: 'warning', message: 'No PDF Component Found' });
        return;
      }
      const { getHeaderDetails, getPropertyLogo, handleShowPDF, getTemplate } =
        formRef.current;

      const template = await getTemplate({
        department: 'FO',
        category: 'Reservations',
        templateName: 'Acknowledgement Receipt',
      });

      if (!template) return;

      const data = (await CASARData({
        params: { casType: tranType || 'AR' },
        query: { casID: ar_no },
      })
        .unwrap()
        .then((result) => result?.data?.[0])) as CASGetARDetails;

      const { propertyLogo } = await getPropertyLogo();
      const headerDetails = getHeaderDetails({ moduleName: 'Xenia FOS' });

      const details: TableRow[] = [
        {
          cells: [
            { value: data.modePayment?.toString() ?? '', id: 'tender' },
            {
              value:
                tranType === 'VAR'
                  ? (data.voidBy?.toString() ?? '')
                  : (data.staffName?.toString() ?? ''),
              id: 'cashier',
            },
            {
              value:
                tranType === 'VAR'
                  ? moment().format(dateTimeFormat)
                  : moment(data.timeStamp).format(dateTimeFormat),
              id: 'timestamp',
            },
            {
              value:
                tranType === 'VAR'
                  ? `Void ${data.refNo} Reason:${data.voidedRemarks}`
                  : (data.remarks?.toString() ?? ''),
              id: 'remarks',
            },
          ],
        },
      ];

      const inputs: IFormInput = {
        ...(propertyLogo && { propertyLogo }),
        ...headerDetails,
        ...toReportInputs(data ?? {}),
        details,
        casAcn,
        casAcnDate,
        arSeries,
        transactionNo: reprint
          ? `${data.transNo}\n* * * R E P R I N T * * *`
          : data.transNo,
        title:
          tranType === 'VAR'
            ? 'VOIDED ACKNOWLEDGEMENT RECEIPT '
            : 'ACKNOWLEDGEMENT RECEIPT',
        gross: `${formatNumber(parseFloat(data.gross), 2, 2)} ${toWords.convert(parseFloat(data.gross)).toUpperCase()}`,
      };

      handleShowPDF({
        template,
        inputs,
      });
    } catch (error) {
      handleCloseClick();
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (!isOpen || !ar_no) return;
    (async () => await handlePrint({ tranType, ar_no }))();
  }, [tranType, ar_no, isOpen]);

  return (
    <FormPDF
      {...{
        ref: formRef,
        title:
          tranType === 'VAR'
            ? 'Voided Acknowledgement Receipt'
            : 'Acknowledgement Receipt',
        modalOnClose: handleCloseClick,
      }}
    />
  );
};

export default CASARVoucher;
